import React from 'react';
import { Calendar, CheckCircle, AlertTriangle, XCircle } from 'lucide-react';

const STATUS_CONFIG = {
    trial_active: { label: 'Trial', bg: 'rgba(34, 197, 94, 0.14)', color: '#86EFAC', icon: CheckCircle },
    active_paid: { label: 'Active', bg: 'rgba(59, 130, 246, 0.16)', color: '#93C5FD', icon: CheckCircle },
    payment_required: { label: 'Payment required', bg: 'rgba(245, 158, 11, 0.18)', color: '#FCD34D', icon: AlertTriangle },
    past_due: { label: 'Past due', bg: 'rgba(245, 158, 11, 0.18)', color: '#FCD34D', icon: AlertTriangle },
    cancelled: { label: 'Cancelled', bg: 'rgba(239, 68, 68, 0.16)', color: '#FCA5A5', icon: XCircle },
    suspended: { label: 'Suspended', bg: 'rgba(239, 68, 68, 0.16)', color: '#FCA5A5', icon: XCircle },
};

function formatDate(dateStr) {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

function formatCurrency(cents, currency = 'usd') {
    if (cents == null) return null;
    const dollars = cents / 100;
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: (currency || 'usd').toUpperCase(),
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(dollars);
}

export function BillingStatusCard({ billing, orgName }) {
    const status = billing?.access_status || 'unknown';
    const config = STATUS_CONFIG[status] || { label: 'Unknown', bg: 'rgba(148, 163, 184, 0.16)', color: '#94A3B8', icon: null };
    const StatusIcon = config.icon;

    const planLabel = billing?.current_plan_label || 'No plan';
    const monthlyAmount = formatCurrency(billing?.monthly_amount_cents, billing?.currency);
    const trialActive = billing?.trial_active;
    const daysRemaining = billing?.days_remaining;
    const periodEnd = billing?.current_period_end;

    let dateText = null;
    if (trialActive && daysRemaining > 0) {
        dateText = `${daysRemaining} day${daysRemaining === 1 ? '' : 's'} remaining`;
    } else if (status === 'active_paid' && periodEnd) {
        dateText = `Renews ${formatDate(periodEnd)}`;
    } else if (status === 'cancelled' && billing?.cancelled_at) {
        dateText = `Cancelled ${formatDate(billing.cancelled_at)}`;
    }

    return (
        <section style={styles.card}>
            <div style={styles.header}>
                <div>
                    <div style={styles.eyebrow}>Account</div>
                    <div style={styles.orgName}>{orgName || 'Organization'}</div>
                </div>
                <span style={{ ...styles.badge, background: config.bg, color: config.color }}>
                    {StatusIcon && <StatusIcon size={14} style={{ marginRight: 6 }} />}
                    {config.label}
                </span>
            </div>

            <div style={styles.divider} />

            <div style={styles.metrics}>
                <div style={styles.metric}>
                    <div style={styles.metricLabel}>Current plan</div>
                    <div style={styles.metricValue}>{planLabel}</div>
                </div>
                {monthlyAmount && (
                    <div style={styles.metric}>
                        <div style={styles.metricLabel}>Monthly</div>
                        <div style={styles.metricValue}>{monthlyAmount}/mo</div>
                    </div>
                )}
                {dateText && (
                    <div style={styles.metric}>
                        <div style={styles.metricLabel}>
                            <Calendar size={12} style={{ marginRight: 4, opacity: 0.7 }} />
                            {trialActive ? 'Trial' : 'Billing'}
                        </div>
                        <div style={styles.metricValue}>{dateText}</div>
                    </div>
                )}
            </div>
        </section>
    );
}

const styles = {
    card: {
        background: 'rgba(37, 99, 235, 0.08)',
        border: '1px solid rgba(96, 165, 250, 0.18)',
        borderRadius: 8,
        padding: 20,
        marginBottom: 24,
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 16,
    },
    eyebrow: {
        fontSize: 12,
        color: '#C3CDDC',
        textTransform: 'uppercase',
        letterSpacing: 0,
        marginBottom: 4,
    },
    orgName: {
        fontSize: 24,
        fontWeight: 700,
        color: '#E2E8F0',
        lineHeight: 1.15,
    },
    badge: {
        display: 'inline-flex',
        alignItems: 'center',
        padding: '6px 10px',
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 700,
        whiteSpace: 'nowrap',
    },
    divider: {
        height: 1,
        background: 'rgba(148, 163, 184, 0.16)',
        margin: '16px 0',
    },
    metrics: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        gap: 16,
    },
    metric: {},
    metricLabel: {
        display: 'flex',
        alignItems: 'center',
        fontSize: 12,
        color: '#C3CDDC',
        marginBottom: 6,
    },
    metricValue: {
        fontSize: 16,
        color: '#E2E8F0',
        fontWeight: 700,
    },
};
